import { useState, useEffect, useCallback } from "react";
import { ArrowsClockwise, Calculator, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import api, { errDetail } from "@/lib/api";

export default function AdminPricing() {
  const [eco, setEco] = useState(null);
  const [prices, setPrices] = useState([]);
  const [sim, setSim] = useState(null);
  const [simRate, setSimRate] = useState(1000);
  const [simMarkup, setSimMarkup] = useState(30);
  const [editRow, setEditRow] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/token-economy").then((r) => { setEco(r.data); setSimRate(r.data.usd_to_tokens); setSimMarkup(r.data.global_markup_pct); });
    api.get("/admin/model-prices").then((r) => setPrices(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveEco = async () => {
    try {
      await api.put("/admin/token-economy", { usd_to_tokens: parseFloat(eco.usd_to_tokens), global_markup_pct: parseFloat(eco.global_markup_pct), price_urls: eco.price_urls });
      toast.success("Token economy saved");
    } catch (e) { toast.error(errDetail(e)); }
  };
  const runSim = async () => {
    setBusy("sim");
    try { const { data } = await api.get("/admin/economics/simulate", { params: { rate: simRate, markup: simMarkup } }); setSim(data); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(""); }
  };
  const refreshPrices = async () => {
    setBusy("refresh");
    try { const { data } = await api.post("/admin/model-prices/refresh"); toast.success(`Refreshed ${data.updated} model prices`); load(); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(""); }
  };
  const savePrice = async (p) => {
    try {
      await api.put(`/admin/model-prices/${p.id}`, {
        input_per_1m: parseFloat(editVals.input_per_1m), output_per_1m: parseFloat(editVals.output_per_1m),
        cached_input_per_1m: parseFloat(editVals.cached_input_per_1m || 0), cache_write_per_1m: parseFloat(editVals.cache_write_per_1m || 0) });
      toast.success("Price updated"); setEditRow(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!eco) return <AdminShell title="Pricing"><div className="text-sm text-gray-400 p-8">Loading...</div></AdminShell>;
  return (
    <AdminShell title="Pricing & Token Economy" testId="admin-pricing-page">
      <div className="border border-gray-200 rounded-md p-5 mb-6" data-testid="economy-card">
        <h2 className="font-heading font-bold text-gray-900 mb-3">Token economy</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">1 USD = X tokens</label>
            <input data-testid="usd-to-tokens-input" type="number" value={eco.usd_to_tokens}
              onChange={(e) => setEco({ ...eco, usd_to_tokens: e.target.value })}
              className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Global markup %</label>
            <input data-testid="markup-input" type="number" value={eco.global_markup_pct}
              onChange={(e) => setEco({ ...eco, global_markup_pct: e.target.value })}
              className="w-28 border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <button data-testid="save-economy-btn" onClick={saveEco}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-semibold">
            <FloppyDisk size={15} /> Save
          </button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-md p-5 mb-6" data-testid="simulator-card">
        <h2 className="font-heading font-bold text-gray-900 mb-1 flex items-center gap-2"><Calculator size={18} /> Pricing simulator</h2>
        <p className="text-xs text-gray-500 mb-3">Uses live prices + your last 30 days of real usage mix. Design packages from these verdicts.</p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div><label className="block text-xs text-gray-500 mb-1">rate (1 USD = X)</label>
            <input data-testid="sim-rate-input" type="number" value={simRate} onChange={(e) => setSimRate(e.target.value)} className="w-28 border border-gray-300 rounded-md px-2 py-1.5 text-sm" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">markup %</label>
            <input data-testid="sim-markup-input" type="number" value={simMarkup} onChange={(e) => setSimMarkup(e.target.value)} className="w-24 border border-gray-300 rounded-md px-2 py-1.5 text-sm" /></div>
          <button data-testid="run-simulator-btn" onClick={runSim} disabled={busy === "sim"}
            className="bg-gray-900 hover:bg-gray-700 text-white rounded-md px-4 py-2 text-sm font-semibold">{busy === "sim" ? "..." : "Recalculate"}</button>
        </div>
        {sim && (
          <div data-testid="simulator-results" className="text-sm space-y-1.5">
            <p>Cost to serve 1,000 tokens: <b>${sim.cost_to_serve_1000_tokens}</b> · You charge: <b>${sim.you_charge_per_1000_tokens}</b> · based on {sim.usage_events_30d} usage events (30d)</p>
            {sim.packages.map((p) => (
              <p key={p.name} data-testid={`sim-pkg-${p.name}`}>
                → {p.name} (${p.price}{p.tokens ? ` · ${p.tokens.toLocaleString()} tokens` : ""}):{" "}
                {p.verdict === "no pool set" ? <span className="text-gray-400">no token pool set</span> : (
                  <>worst-case cost ${p.worst_case_cost} → <b className={p.margin > 0 ? "text-emerald-600" : "text-red-600"}>{p.margin > 0 ? "✅" : "❌"} {p.margin >= 0 ? "+" : ""}${p.margin}</b></>)}
              </p>
            ))}
            {sim.token_packs.map((p) => (
              <p key={p.name}>→ Pack {p.name} (${p.price} · {p.tokens.toLocaleString()}): cost ${p.worst_case_cost} → <b className={p.margin > 0 ? "text-emerald-600" : "text-red-600"}>{p.margin >= 0 ? "+" : ""}${p.margin}</b></p>
            ))}
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-md p-5" data-testid="prices-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-heading font-bold text-gray-900">Model pricing <span className="text-xs font-normal text-gray-400">(auto-refresh daily 00:00 UTC{eco.prices_last_refresh ? ` · last: ${eco.prices_last_refresh.slice(0, 16)}` : ""})</span></h2>
          <button data-testid="refresh-prices-btn" onClick={refreshPrices} disabled={busy === "refresh"}
            className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 text-sm font-semibold">
            <ArrowsClockwise size={14} className={busy === "refresh" ? "animate-spin" : ""} /> Refresh now
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-2 mb-4">
          {["openrouter", "anthropic", "openai"].map((k) => (
            <div key={k}><label className="block text-[10px] uppercase text-gray-400 mb-0.5">{k} source URL</label>
              <input data-testid={`price-url-${k}`} value={eco.price_urls?.[k] || ""} onChange={(e) => setEco({ ...eco, price_urls: { ...eco.price_urls, [k]: e.target.value } })}
                className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-gray-600" /></div>
          ))}
        </div>
        {(eco.prices_refresh_failed || []).length > 0 && <p className="text-xs text-amber-600 mb-2">⚠ {eco.prices_refresh_failed.join(", ")}: refresh failed — using last known values, edit manually below.</p>}
        <div className="max-h-96 overflow-y-auto scroll-thin">
          <table className="w-full text-xs" data-testid="prices-table">
            <thead className="text-left text-gray-400 uppercase sticky top-0 bg-white">
              <tr><th className="py-1.5">Provider</th><th>Model</th><th>Input</th><th>Cached-in</th><th>Cache-w</th><th>Output</th><th></th></tr>
            </thead>
            <tbody>
              {prices.map((p) => (
                <tr key={p.id} className="border-t border-gray-100" data-testid={`price-row-${p.provider}-${p.model}`}>
                  <td className="py-1.5 text-gray-500">{p.provider}</td>
                  <td className="font-medium text-gray-800 max-w-[220px] truncate">{p.model}{p.manual_override && <span className="ml-1 text-[9px] bg-blue-50 text-blue-600 rounded px-1">manual</span>}</td>
                  {editRow === p.id ? (
                    <>
                      {["input_per_1m", "cached_input_per_1m", "cache_write_per_1m", "output_per_1m"].map((f) => (
                        <td key={f}><input value={editVals[f]} onChange={(e) => setEditVals({ ...editVals, [f]: e.target.value })} className="w-16 border border-gray-300 rounded px-1 py-0.5" /></td>
                      ))}
                      <td><button data-testid={`price-save-${p.id}`} onClick={() => savePrice(p)} className="text-blue-600 font-bold">save</button></td>
                    </>
                  ) : (
                    <>
                      <td>${p.input_per_1m?.toFixed(2)}</td><td>${p.cached_input_per_1m?.toFixed(2)}</td>
                      <td>${p.cache_write_per_1m?.toFixed(2)}</td><td>${p.output_per_1m?.toFixed(2)}</td>
                      <td><button data-testid={`price-edit-${p.id}`} onClick={() => { setEditRow(p.id); setEditVals(p); }} className="text-gray-400 hover:text-blue-600">✎</button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Per 1M tokens, USD. OpenRouter auto-refreshes from its models API; OpenAI/Anthropic use seeded values — edit manually when providers change pricing.</p>
      </div>
    </AdminShell>
  );
}
